import { useEffect, useState, useRef } from "react";
import { Carousel, OverlayTrigger, Popover } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";
import { trackEvent } from "../../../services/events";
import { toast } from "react-toastify";
import ImagePopupModal from "../../MerchantDashboard/ImagePopupModal";
import "./styles.css";
import {
  dashboard_invoice_green_tick,
  dashboard_invoice_order_date,
  dashboard_invoice_order_history,
  merchant_dashboard_location_icon,
  pdf,
  manage_my_info_edit_logo,
} from "../../../assets/images";

const usePdfDownload = () => {
  const downloadPdf = async (name: string, url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = `${name}.pdf`;
    link.target = "_self";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    loading: false,
    downloadedUri: null as string | null,
    lastFileName: null as string | null,
    downloadPdf,
    clearBanner: () => {},
  };
};

const RentDealsDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const OrderId = new URLSearchParams(location.search).get("OrderId");
  const refpopup = useRef(null);

  const { data: invoiceDetails } = useQuery({
    queryKey: ["invoiceDetails", OrderId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${API_URL.CUSTOMER_ORDER_DETAILS}/${OrderId}/details`,
        {
          headers: {
            location: "0,0",
            language: "english",
          },
        }
      );
      return response.data;
    },
  });

  const [orderStatusBooked, setOrderStatusBooked] = useState(false);
  const [orderStatusAccepted, setOrderStatusAccepted] = useState(false);
  const [orderStatusDelivered, setOrderStatusDelivered] = useState(false);
  const [orderStatusReCollected, setOrderStatusReCollected] = useState(false);
  const [orderStatusTimeline, setOrderStatusTimeline] = useState("");
  const [showImagePopupModal, setShowImagePopupModal] = useState(false);
  const [imageSource, setImageSource] = useState("");

  useEffect(() => {
    if (invoiceDetails?.orderStatus) {
      let timeline = "";
      let booked = false;
      let accepted = false;
      let delivered = false;
      let recollected = false;
      switch (invoiceDetails.orderStatus) {
        case "BOOKED":
          timeline = "time-line-0";
          booked = true;
          break;
        case "ORDER_ACCEPT":
        case "MERCHANT_ORDER_ACCEPT":
        case "MERCHANT_ORDER_REJECTED":
          timeline = "time-line-25";
          booked = true;
          accepted = true;
          break;
        case "DELIVERED":
          timeline = "time-line-75";
          booked = true;
          accepted = true;
          delivered = true;
          break;
        case "RECOLLECTED":
          timeline = "time-line-100";
          booked = true;
          accepted = true;
          delivered = true;
          recollected = true;
          break;
        case "CANCELED":
          timeline = "";
          booked = false;
          accepted = false;
          delivered = false;
          recollected = false;
          break;
        default:
          timeline = "";
          booked = false;
          accepted = false;
          delivered = false;
          recollected = false;
          break;
      }
      setOrderStatusTimeline(timeline);
      setOrderStatusBooked(booked);
      setOrderStatusAccepted(accepted);
      setOrderStatusDelivered(delivered);
      setOrderStatusReCollected(recollected);
    }
  }, [invoiceDetails?.orderStatus]);

  useEffect(() => {
    const trackPageLoad = async () => {
      await trackEvent({
        event_name: { web: "Kirkos_TrackOrderScreen", app: "Kirkos_TrackOrderScreen_App" },
        event_category: "TrackOrder_Info",
        module: "TrackOrder",
        payload: {},
      });
    };
    trackPageLoad();
  }, []);

  // Document Fetching Effects
  useEffect(() => {
    if (invoiceDetails?.customer_details?.customerDocs) {
      const customerDocs = invoiceDetails?.customer_details?.customerDocs;
      const docGroups = [
        Object.keys(customerDocs).filter((key) => key.startsWith("EMIRITIES")),
        Object.keys(customerDocs).filter((key) => key.startsWith("LICENSE")),
        Object.keys(customerDocs).filter((key) => key.startsWith("VisitVisa")),
        Object.keys(customerDocs).filter((key) => key.startsWith("Passport")),
      ];

      docGroups.flat().forEach((docKey) => {
        const documentId = customerDocs[docKey];
        if (documentId !== undefined && documentId !== null) {
          axiosInstance
            .get(`${API_URL.CUSTOMER_DOCUMENTS}/${documentId}`, {
              responseType: "arraybuffer",
            })
            .then((response) => {
              if (response && response.data) {
                const contentType = String(response.headers["content-type"] || "");
                const isPdf = contentType === "application/pdf";
                const isImage = contentType.startsWith("image/");

                if (isPdf) {
                  const blob = new Blob([response.data], { type: "application/pdf" });
                  const objectUrl = URL.createObjectURL(blob);
                  const downloadLink = document.getElementById(`${docKey}DownloadLink`) as HTMLAnchorElement;
                  if (downloadLink) {
                    downloadLink.href = objectUrl;
                    downloadLink.style.display = "block";
                    downloadLink.download = `${docKey}.pdf`;
                  }
                  const imageElement = document.getElementById(`${docKey}_IMAGE`) as HTMLImageElement;
                  if (imageElement) imageElement.src = pdf;
                } else if (isImage) {
                  const blob = new Blob([response.data], { type: contentType });
                  const imageUrl = URL.createObjectURL(blob);
                  const imageElement = document.getElementById(`${docKey}_IMAGE`) as HTMLImageElement;
                  if (imageElement) {
                    imageElement.src = imageUrl;
                    imageElement.onclick = () => {
                      setImageSource(imageUrl);
                      setShowImagePopupModal(true);
                    };
                  }
                }
              }
            })
            .catch((error) => console.error(`Error fetching ${docKey}:`, error));
        }
      });
    }
  }, [invoiceDetails]);

  const formatDate = (isoDate: any) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year}, ${hours}:${minutes}:${seconds}`;
  };

  const handleDownloadInvoice = async () => {
    try {
      if (!invoiceDetails) {
        toast.error("Invoice details not available");
        return;
      }
      const invoiceData = invoiceDetails?.order_invoice_data;
      const paymentInitiatedUrls = invoiceData?.PAYMENT_INITIATED;

      if (!paymentInitiatedUrls || !Array.isArray(paymentInitiatedUrls) || paymentInitiatedUrls.length === 0) {
        toast.error("Invoice is not available yet. Please try again later.");
        return;
      }

      await trackEvent({
        event_name: {
          web: "Kirkos_RentDeals_Invoice_Downloaded",
          app: "Kirkos_RentDeals_Invoice_Downloaded_App",
        },
        event_category: "RentDeals_Info",
        module: "RentDeals",
        payload: {},
      });
      const invoiceUrl = paymentInitiatedUrls[0];
      await downloadPdf("Invoice", invoiceUrl);
    } catch (err) {
      console.error("Failed to open invoice:", err);
      toast.error("Failed to open invoice. Please try again.");
    }
  };

  const { loading, downloadedUri, downloadPdf, clearBanner } = usePdfDownload();

  const popoverHoverFocus = (
    <Popover id="popover-trigger-hover-focus" className="popover-custom-edit">
      <Popover.Body className="d-flex align-items-center">
        <span className="invoice-div-data text-light">Edited by Kirkos backoffice</span>
      </Popover.Body>
    </Popover>
  );


  const overlayStyle = {
    position: "fixed",
    inset: 0 as any,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };
  const spinnerStyle = {
    width: 40,
    height: 40,
    border: "4px solid #eee",
    borderTop: "4px solid #000",
    borderRadius: "50%",
  };
  const bannerStyle = {
    position: "fixed",
    bottom: 20,
    left: 16,
    right: 16,
    background: "#5f5c5cff",
    color: "#fff",
    padding: "12px 16px",
    borderRadius: 8,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    zIndex: 9999,
  };
  const viewButtonStyle = {
    background: "transparent",
    border: "none",
    color: "#f5b719ff",
    fontWeight: "bold" as const,
    cursor: "pointer",
  };

  return (
    <div className="col-md-12 text-start div-bg-invoice-details">
      {loading && (
        <div style={overlayStyle as any}>
          <div style={spinnerStyle as any} />
          <p className="text-light">Downloading invoice…</p>
        </div>
      )}
      {downloadedUri && (
        <div style={bannerStyle as any}>
          <span>
            Invoice downloaded
            <span style={{ display: "block", fontSize: 11, color: "#888" }}>{downloadedUri}</span>
          </span>
          <div>
            <button
              onClick={() => {
                const q = new URLSearchParams({ name: "Invoice", uri: downloadedUri, mime: "application/pdf" }).toString();
                navigate(`/customer/documents/view?${q}`);
                clearBanner();
              }}
              style={{ ...(viewButtonStyle as any), padding: "8px 12px" }}
            >
              VIEW
            </button>
            <button onClick={() => clearBanner()} style={{ ...(viewButtonStyle as any), color: "#bbb" }}>
              DISMISS
            </button>
          </div>
        </div>
      )}

      <div className="card card-bg-invoice-details" ref={refpopup}>
        <div className="card-body px-3">
          <table className="invoice-table-width">
            <tbody>
              <tr className="invoice-table-tr-first">
                <td className="invoice-table-td">Order Number #</td>
                <td className="text-end">Order Created At</td>
              </tr>
              <tr>
                <td className="invoice-text-yellow">{invoiceDetails?.orderId}</td>
                <td className="text-end">{formatDate(invoiceDetails?.createdAt)}</td>
              </tr>
            </tbody>
          </table>
          <div className="row pt-3">
            <div className="col-md-6 pt-3 align-content-center px-4">
              <Carousel className="managemycarlist" controls={false}>
                {invoiceDetails?.products?.[0]?.productImageUrls?.map((imageUrl: any, index: number) => (
                  <Carousel.Item className="image-managemycar-height" key={index}>
                    <img className="img-merchant-managecar" src={imageUrl} alt={`ProductImage${index + 1}`} />
                  </Carousel.Item>
                ))}
              </Carousel>
              <div className={`invoice-text-yellow d-flex align-items-center pt-2 ${invoiceDetails?.latest_modified_field_flags?.productId ? "text-danger" : ""}`}>
                {invoiceDetails?.products?.[0]?.productName}
                {invoiceDetails?.modified_field_flags?.productId && (
                  <OverlayTrigger container={refpopup} trigger={["hover"]} placement="top" overlay={popoverHoverFocus}>
                    <div className="ps-2">
                      <img src={manage_my_info_edit_logo} alt="Edit" width="18" />
                    </div>
                  </OverlayTrigger>
                )}
              </div>
            </div>
            <div className="col-md-6 pt-3 align-content-center">
              <div className={orderStatusBooked ? "d-none" : "card-timeline text-center"}></div>
              {invoiceDetails?.orderStatus === "CANCELED" ? (
                <div className="card-timeline text-center">
                  <div className="pt-3 pb-3">Your Order has been Cancelled</div>
                </div>
              ) : (
                <div className={orderStatusBooked ? "card-timeline" : "d-none"}>
                  <div className="card-body-timeline">
                    <div className={`vertical-timeline vertical-timeline--animate vertical-timeline--one-column ${orderStatusTimeline}`}>
                      <div className="vertical-timeline-item vertical-timeline-element">
                        <div>
                          <span className="vertical-timeline-element-icon bounce-in">
                            <i className={`badge badge-dot badge-dot-xl badge-success ${orderStatusBooked ? "badge-dot-yellow" : "badge-dot-gray"}`}> </i>
                          </span>
                          <div className="vertical-timeline-element-content bounce-in">
                            <h4 className={`timeline-title ${orderStatusBooked ? "text-colour-timeline" : "text-colour-timeline-gray"}`}>Booked</h4>
                          </div>
                        </div>
                      </div>
                      <div className="vertical-timeline-item vertical-timeline-element">
                        <div>
                          <span className="vertical-timeline-element-icon bounce-in">
                            <i className={`badge badge-dot badge-dot-xl badge-success ${orderStatusAccepted ? "badge-dot-yellow" : "badge-dot-gray"}`}> </i>
                          </span>
                          <div className="vertical-timeline-element-content bounce-in">
                            <h4 className={`timeline-title ${orderStatusAccepted ? "text-colour-timeline" : "text-colour-timeline-gray"}`}>Accepted</h4>
                          </div>
                        </div>
                      </div>
                      <div className="vertical-timeline-item vertical-timeline-element">
                        <div>
                          <span className="vertical-timeline-element-icon bounce-in">
                            <i className={`badge badge-dot badge-dot-xl badge-success ${orderStatusDelivered ? "badge-dot-yellow" : "badge-dot-gray"}`}> </i>
                          </span>
                          <div className="vertical-timeline-element-content bounce-in">
                            <h4 className={`timeline-title ${orderStatusDelivered ? "text-colour-timeline" : "text-colour-timeline-gray"}`}>Deliverd</h4>
                          </div>
                        </div>
                      </div>
                      <div className="vertical-timeline-item vertical-timeline-element">
                        <div>
                          <span className="vertical-timeline-element-icon bounce-in">
                            <i className={`badge badge-dot badge-dot-xl badge-success ${orderStatusReCollected ? "badge-dot-yellow" : "badge-dot-gray"}`}> </i>
                          </span>
                          <div className="vertical-timeline-element-content bounce-in">
                            <h4 className={`timeline-title ${orderStatusReCollected ? "text-colour-timeline" : "text-colour-timeline-gray"}`}>Recollected</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="row pt-3">
            <div className="col-md-6">
              <div className={`invoice-div-head pt-3 d-flex align-items-center ${invoiceDetails?.latest_modified_field_flags?.delivery_location ? "text-danger" : ""}`}>
                Location
                {invoiceDetails?.modified_field_flags?.delivery_location && (
                  <OverlayTrigger container={refpopup} trigger={["hover"]} placement="top" overlay={popoverHoverFocus}>
                    <div className="ps-2"><img src={manage_my_info_edit_logo} alt="Edit" width="18" /></div>
                  </OverlayTrigger>
                )}
              </div>
              <div className="invoice-div-data">{invoiceDetails?.delivery_location?.address}</div>

              <div className={`invoice-div-head pt-3 d-flex align-items-center ${invoiceDetails?.latest_modified_field_flags?.rental_days ? "text-danger" : ""}`}>
                Number of Days
                {invoiceDetails?.modified_field_flags?.rental_days && (
                  <OverlayTrigger container={refpopup} trigger={["hover"]} placement="top" overlay={popoverHoverFocus}>
                    <div className="ps-2"><img src={manage_my_info_edit_logo} alt="Edit" width="18" /></div>
                  </OverlayTrigger>
                )}
              </div>
              <div className="invoice-div-data">
                {invoiceDetails?.rental_days - (invoiceDetails?.extended_rental_days || 0)}
              </div>

              {invoiceDetails?.extended_rental_days !== null && invoiceDetails?.extended_rental_days !== 0 && (
                <>
                  <div className="invoice-div-head pt-3">Extended Rental Days</div>
                  <div className="invoice-div-data">{invoiceDetails?.extended_rental_days}</div>
                  <div className="invoice-div-head pt-3">Total Rental Days</div>
                  <div className="invoice-div-data">{invoiceDetails?.rental_days}</div>
                </>
              )}

              <div className="invoice-div-head pt-3">Merchant Name</div>
              <div className="invoice-div-data">{invoiceDetails?.merchantDetails?.shop_info?.shop_name}</div>
              <div className="invoice-div-head pt-3">Merchant Mail</div>
              <div className="invoice-div-data">{invoiceDetails?.merchantDetails?.merchant_info?.email}</div>
              <div className="invoice-div-head pt-3">Payment Status</div>
              <div className="invoice-div-data">{invoiceDetails?.payment_status}</div>
              <div className="invoice-div-head pt-3">Payment Method</div>
              <div className="invoice-div-data">{invoiceDetails?.payment_method}</div>
              <div className="invoice-div-head pt-3">Paid Amount</div>
              <div className="invoice-div-data">{invoiceDetails?.order_total_amount?.toFixed(2)}</div>
            </div>

            <div className="col-md-6 border-start-invoice-details pt-4">
              <div className="invoice-div-head">Customer Name</div>
              <div className="invoice-div-data">{invoiceDetails?.customer_name}</div>
              <div className="invoice-div-head pt-3">Customer Phone Number</div>
              <div className="invoice-div-data">{invoiceDetails?.customer_phone_number}</div>

              {/* <div className="invoice-div-head pt-3">Delivery Time</div>
              <div className="invoice-div-data">{invoiceDetails?.delivery_preferred_time}</div> */}

              {invoiceDetails?.delivered_by && (
                <>
                  <div className={`invoice-div-head pt-3 d-flex align-items-center ${invoiceDetails?.latest_modified_field_flags?.delivered_by ? "text-danger" : ""}`}>
                    {invoiceDetails?.delivered_by === "CUSTOMER" ? "Pickup By" : "Delivery By"}
                    {invoiceDetails?.modified_field_flags?.delivered_by && (
                      <OverlayTrigger container={refpopup} trigger={["hover"]} placement="top" overlay={popoverHoverFocus}>
                        <div className="ps-2"><img src={manage_my_info_edit_logo} alt="Edit" width="18" /></div>
                      </OverlayTrigger>
                    )}
                  </div>
                  <div className="invoice-div-data">{invoiceDetails?.delivered_by}</div>
                </>
              )}

              <div className={`invoice-div-head d-flex align-items-center pt-3 
                ${invoiceDetails?.latest_modified_field_flags?.babySeat || invoiceDetails?.latest_modified_field_flags?.additionalDriver || invoiceDetails?.latest_modified_field_flags?.cdwAmount || invoiceDetails?.latest_modified_field_flags?.windShieldProtection || invoiceDetails?.latest_modified_field_flags?.scdwAmount || invoiceDetails?.latest_modified_field_flags?.paiInsuranceAmount ? "text-danger" : ""}`}>
                Additional Options
                {(invoiceDetails?.modified_field_flags?.babySeat || invoiceDetails?.modified_field_flags?.additionalDriver || invoiceDetails?.modified_field_flags?.cdwAmount || invoiceDetails?.modified_field_flags?.windShieldProtection || invoiceDetails?.modified_field_flags?.scdwAmount || invoiceDetails?.modified_field_flags?.paiInsuranceAmount) && (
                  <OverlayTrigger container={refpopup} trigger={["hover"]} placement="top" overlay={popoverHoverFocus}>
                    <div className="ps-2"><img src={manage_my_info_edit_logo} alt="Edit" width="18" /></div>
                  </OverlayTrigger>
                )}
              </div>
              <div className="invoice-div-data">
                <ul style={{ paddingLeft: "20px", marginBottom: "0", lineHeight: "1.8" }}>
                  {invoiceDetails?.products[0]?.babySeatAmount > 0 && <li style={{ marginBottom: "8px" }}>Baby Seat</li>}
                  {invoiceDetails?.products[0]?.paiInsuranceAmount > 0 && <li style={{ marginBottom: "8px" }}>PAI Insurance</li>}
                  {invoiceDetails?.products[0]?.windShieldProtection > 0 && <li style={{ marginBottom: "8px" }}>WindShield Protection</li>}
                  {invoiceDetails?.products[0]?.additionalDriverAmount > 0 && <li style={{ marginBottom: "8px" }}>Additional Driver</li>}
                  {invoiceDetails?.products[0]?.scdwAmount > 0 && <li style={{ marginBottom: "8px" }}>SCDW Insurance</li>}
                  {invoiceDetails?.products[0]?.cdwAmount > 0 && <li style={{ marginBottom: "8px" }}>CDW Insurance</li>}
                </ul>
              </div>
            </div>
          </div>

          <div className="row pt-3">
            <div className="col-12 pt-5">
              <div className="order-history-card">
                <div className="order-history-header">
                  <div className="d-flex align-items-center gap-3">
                    <div className="order-history-icon-wrapper">
                      <img src={dashboard_invoice_order_history} height={"20px"} alt="Order History" />
                    </div>
                    <h3 className="order-history-title">Order History</h3>
                  </div>
                </div>

                <div className="order-history-body">
                  {/* Timeline Container */}
                  <div className="order-timeline">
                    {/* Purchase Order Date */}
                    <div className="timeline-item">
                      <div className="timeline-icon-container">
                        <div className="timeline-icon">
                          <img src={dashboard_invoice_order_date} height={"20px"} alt="Date" />
                        </div>
                        <div className="timeline-line"></div>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-label">Purchase Order Date</div>
                        <div className="timeline-value">{formatDate(invoiceDetails?.createdAt)}</div>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="timeline-item">
                      <div className="timeline-icon-container">
                        <div className="timeline-icon status-icon">
                          <img src={dashboard_invoice_green_tick} height={"20px"} alt="Status" />
                        </div>
                        <div className="timeline-line"></div>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-label">Status</div>
                        <div className="timeline-value status-value">{invoiceDetails?.orderStatus}</div>
                      </div>
                    </div>

                    {/* Delivery Date & Location */}
                    <div className="timeline-item">
                      <div className="timeline-icon-container">
                        <div className="timeline-icon">
                          <img src={merchant_dashboard_location_icon} height={"20px"} alt="Location" />
                        </div>
                        <div className="timeline-line"></div>
                      </div>
                      <div className="timeline-content">
                        <div className={`timeline-label d-flex align-items-center gap-2 ${invoiceDetails?.latest_modified_field_flags?.delivery_location || invoiceDetails?.latest_modified_field_flags?.delivery_date ? "text-danger" : ""}`}>
                          <span>Delivery Date & Location</span>
                          {(invoiceDetails?.modified_field_flags?.delivery_location || invoiceDetails?.modified_field_flags?.delivery_date) && (
                            <OverlayTrigger container={refpopup} trigger={["hover"]} placement="top" overlay={popoverHoverFocus}>
                              <img src={manage_my_info_edit_logo} alt="Edit" width="16" className="edit-indicator" />
                            </OverlayTrigger>
                          )}
                        </div>
                        <div className="timeline-value location-value">
                          <span className="timeline-date">{formatDate(invoiceDetails?.delivery_date)}</span>
                          <span className="timeline-separator">|</span>
                          <span className="timeline-address">{invoiceDetails?.delivery_location?.address}</span>
                        </div>
                      </div>
                    </div>

                    {/* Recollection Date & Location */}
                    <div className="timeline-item timeline-item-last">
                      <div className="timeline-icon-container">
                        <div className="timeline-icon">
                          <img src={merchant_dashboard_location_icon} height={"20px"} alt="Location" />
                        </div>
                      </div>
                      <div className="timeline-content">
                        <div className={`timeline-label d-flex align-items-center gap-2 ${invoiceDetails?.latest_modified_field_flags?.recollection_location || invoiceDetails?.latest_modified_field_flags?.recollection_date ? "text-danger" : ""}`}>
                          <span>Recollection Date & Location</span>
                          {(invoiceDetails?.modified_field_flags?.recollection_location || invoiceDetails?.modified_field_flags?.recollection_date) && (
                            <OverlayTrigger container={refpopup} trigger={["hover"]} placement="top" overlay={popoverHoverFocus}>
                              <img src={manage_my_info_edit_logo} alt="Edit" width="16" className="edit-indicator" />
                            </OverlayTrigger>
                          )}
                        </div>
                        <div className="timeline-value location-value">
                          <span className="timeline-date">{formatDate(invoiceDetails?.recollection_date)}</span>
                          <span className="timeline-separator">|</span>
                          <span className="timeline-address">{invoiceDetails?.recollection_location?.address}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="order-history-footer">
                  <button
                    className="btn invoice-download-btn"
                    onClick={handleDownloadInvoice}
                    disabled={!invoiceDetails}
                  >
                    <span>Download Invoice</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ImagePopupModal isOpen={showImagePopupModal} src={imageSource} onClose={() => setShowImagePopupModal(false)} message={""} />
    </div>
  );
};

export default RentDealsDetails;
